import { TripContext } from 'contexts'
import { useContext } from 'react'
import { ImageUpload, Order, OrderItem, OrderItemChange, Stop, SyncStatus, Trip } from './types'
import { OrderItemChangeImage } from 'services/types'
import { SyncingStatus } from './enums/syncingStatus'
import { FailedRequestType } from './enums'
import { authentication, converting, errorLogging, isOnline } from 'utils';
import { BlobStorageUploadFileResult, BlobStorageDownloadFileResult } from './types/responses'
import axiosInstance from 'utils/axiosConfig'
import { envConfig } from 'config'

export const useImageUploadService = () => {

    const config = envConfig();
    const serviceUrl = `${config.REACT_APP_FILE_SERVICE}`;
    const tripContext = useContext(TripContext)
    const { handleHttpError } = errorLogging()


    const addImageToTheTripContext = (file: File, orderItemChange: OrderItemChange) => {
        let reader = new FileReader()
        reader.onload = () => {
            const orderItem: OrderItem = tripContext.orderItems.find(oi => oi.id == orderItemChange.orderItemId) ?? {} as OrderItem
            const order: Order = tripContext.orders.find(o => o.id == orderItem.orderId) ?? {} as Order
            const stop: Stop = tripContext.stops.find(s => s.id == order.stopId) ?? {} as Stop
            const trip: Trip = tripContext.trips.find(t => t.id == stop.tripId) ?? {} as Trip
            const uploadedImage: OrderItemChangeImage = {
                id: new Date().getTime(),
                file: reader.result!.toString(),
                objectUrl: URL.createObjectURL(file),
                orderItemChangeId: orderItemChange.id,
                orderItemId: orderItemChange.orderItemId,
                reconcileId: orderItemChange.reconcileReasonId,
                loadNumber: trip.loadNumber,
                orderNumber: order.orderNumber,
                itemNumber: orderItem.lineItemId,
                filenameInBlobStorage: ""
            }

            uploadedImage && tripContext.orderItemChangeImages.put(uploadedImage)
        }
        reader.readAsDataURL(file)
    }

    const removeAllUploadedImagesByOrderItemChangeId = (orderItemChangeId: number) => {
        let uploadedImages: OrderItemChangeImage[] = tripContext.orderItemChangeImages.filter(x => x.orderItemChangeId == orderItemChangeId)!
        let uploadedImageIds: number[] = uploadedImages.map(x => x.id)

        tripContext.orderItemChangeImages.bulkRemove(uploadedImageIds)
    }

    const removeUploadedImageById = (uploadedImageId: number) => {
        tripContext.orderItemChangeImages.remove(uploadedImageId)
    }

    const getImgUrl = (uploadedImageId: number): string => {
        var uploadedImage: OrderItemChangeImage = tripContext.orderItemChangeImages.find(x => x.id == uploadedImageId)!
        return uploadedImage.objectUrl;
    }

    const sendImagesToOnBase = async (images: OrderItemChangeImage[]): Promise<boolean> => {
        let result: boolean = false
        if(images && images.length > 0) {
          const imagesToSendToOnbase = converting.convertChangeImageFilesToBlobObjects(images)
          const imageUploadObjects: ImageUpload[] = await converting.convertToImageUploadObject(imagesToSendToOnbase)
  
          imageUploadObjects.forEach(async upload => {
              let recordIdString: string = ''
              Object.entries(upload.keyValues).forEach(([key, value]) => {
                  recordIdString += value.value
              }); 
  
              const recordId: number = parseInt(recordIdString)
              const formData: FormData =  await converting.convertUploadImageObjectsToFormData(upload)
              const oldImageUploads: SyncStatus[] = tripContext.syncStatus.filter(x => recordId == x.id && x.type == FailedRequestType.ImageUpload) ?? {} as SyncStatus[]
              let response = await axiosInstance.post(`${serviceUrl}/FilesAsync`, formData, { headers: await authentication.getAuthHeader('multipart/form-data')}).catch(handleHttpError)
             
              if(response && response.status == 200) {
                  var imageIdsToRemove: number[] = images.reduce((ids: number[], image: OrderItemChangeImage) => {
                      if (parseInt(`${image.loadNumber}${image.orderNumber}${image.itemNumber}`) == recordId) {
                        ids.push(image.id);
                      }
                      return ids;
                    }, []);
  
                  tripContext.syncStatus.bulkRemove(oldImageUploads.map(x => x.id))
                  tripContext.orderItemChangeImages.bulkRemove(imageIdsToRemove)
                  result = true
              } 
              else {
                  if(oldImageUploads && oldImageUploads.length > 0) {
                      tripContext.syncStatus.bulkRemove(oldImageUploads.map(x => x.id))
                      tripContext.syncStatus.put({id: recordId, data: images.filter(x => parseInt(`${x.loadNumber}${x.orderNumber}${x.itemNumber}`) == recordId), status: SyncingStatus.FailedToSync, type: FailedRequestType.ImageUpload})
                  } else {
                      tripContext.syncStatus.put({id: recordId, data: images.filter(x => parseInt(`${x.loadNumber}${x.orderNumber}${x.itemNumber}`) == recordId), status: SyncingStatus.FailedToSync, type: FailedRequestType.ImageUpload})
                  }
              }
          })
        }
  
        return result
    }

    const sendImagesToBlobStorage = async (images: OrderItemChangeImage[]): Promise<BlobStorageUploadFileResult[]> => {
        var blobStorageUploadFileResults: BlobStorageUploadFileResult[] = []

        if(images && images.length > 0) {
  
          const imagesToSendToOnbase = converting.convertChangeImageFilesToBlobObjects(images)
          const imageUploadObjects: ImageUpload[] = await converting.convertToImageUploadObject(imagesToSendToOnbase)

            for (const imageUploadObject of imageUploadObjects) {
                const formData: FormData =  await converting.convertUploadImageObjectsToFormData(imageUploadObject, false)
  
                let response = await axiosInstance.post(`${serviceUrl}/FilesBlobStorage`,
                formData, { headers: await authentication.getAuthHeader('multipart/form-data')}).catch(handleHttpError)

                if(response && response.status == 200) {
                    const results = JSON.parse(JSON.stringify(response.data.result)) as BlobStorageUploadFileResult[];
                    blobStorageUploadFileResults = blobStorageUploadFileResults.concat(results)
                } 
            }

        }
  
        return blobStorageUploadFileResults
    }

    const getImagesFromBlobStorage = async (fileNameInBlobStorage: string, orderItemChangeImage: OrderItemChangeImage): Promise<BlobStorageDownloadFileResult> => { 
        var blobStorageDownloadFileResult: BlobStorageDownloadFileResult = {} as BlobStorageDownloadFileResult;
        let reader = new FileReader();
        
        // we have to specify the type of response to Axios, lest we get encoded, unusable data
        let response = await axiosInstance.get(`${serviceUrl}/File/${fileNameInBlobStorage}`, {
            responseType: "arraybuffer"
        });
        
        if(response && response.status == 200)
        {
            // we must transform our array buffer (byte array) from the File Service into something we can use
            let bytes = new Uint8Array(response.data);

            let blob = new Blob([bytes] , {type:'image/jpeg'}) 

            // our File Reader expects a File object, so we must make one using our Blob object
            const file = new File([blob], fileNameInBlobStorage, {type:'image/jpeg'});

            reader.onload = () => {
                
                blobStorageDownloadFileResult.file = reader.result!.toString();
                blobStorageDownloadFileResult.objectUrl = URL.createObjectURL(file);

                orderItemChangeImage.file = blobStorageDownloadFileResult.file;
                orderItemChangeImage.objectUrl = blobStorageDownloadFileResult.objectUrl;
                
                tripContext.orderItemChangeImages.put(orderItemChangeImage);
            }
            reader.readAsDataURL(file);
        }

        return blobStorageDownloadFileResult;
    }  

   
    return {
        addImageToTheTripContext: addImageToTheTripContext,
        removeAllUploadedImagesByOrderItemChangeId: removeAllUploadedImagesByOrderItemChangeId,
        removeUploadedImageById: removeUploadedImageById,
        getImgUrl: getImgUrl,
        sendImagesToOnBase: sendImagesToOnBase,
        sendImagesToBlobStorage: sendImagesToBlobStorage,
        getImagesFromBlobStorage: getImagesFromBlobStorage
    }

}